// src/layout/BottomTabs.jsx
import React from "react";
import { THEME } from "../ui/theme";

const TAB_H = 64;

const SAFE_B = "var(--sab, 0px)";
const SAFE_L = "var(--sal, 0px)";
const SAFE_R = "var(--sar, 0px)";

const isIOS =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) &&
  !window.MSStream;

export const BOTTOM_TABS_H = TAB_H;

function tabStyle(isActive, isCompact = false) {
  return {
    height: "100%",
    textAlign: "center",
    padding: isCompact ? "0 12px" : "0 10px",
    textDecoration: "none",
    color: isActive ? THEME.text : THEME.muted,
    fontSize: 11,
    fontWeight: 900,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    WebkitTapHighlightColor: "transparent",
    userSelect: "none",
    cursor: "pointer",
    borderRadius: 12,
    margin: 6,
    border: isActive ? `1px solid rgba(255,255,255,0.20)` : "1px solid transparent",
    background: isActive ? "rgba(255,255,255,0.10)" : "transparent",
    minWidth: isCompact ? 86 : 0,
  };
}

/**
 * BottomTabs (submenus da área)
 *
 * props:
 * - items: [{ key, label, to?, icon? }]
 * - value: key ativo
 * - onChange: (key, item) => void
 *
 * Observação:
 * - `to` é opcional (quem navega é o onChange do AppShell).
 */
export default function BottomTabs({ items = [], value, onChange }) {
  if (!items?.length) return null;

  const compact = items.length > 4;

  const containerStyle = compact
    ? {
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
      }
    : { overflow: "hidden" };

  return (
    <nav
      aria-label="Submenu"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        height: `calc(${TAB_H}px + ${SAFE_B})`,
        paddingLeft: SAFE_L,
        paddingRight: SAFE_R,

        /* ✅ separador claro (igual ao topbar) */
        borderTop: `1px solid ${THEME.border}`,

        /* ✅ base sólida para não “colar” no conteúdo */
        background: THEME.bg,
      }}
    >
      {/* hairline suave pra dar acabamento (opcional, mas ajuda muito) */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          height: 1,
          background: "rgba(255,255,255,0.06)",
          pointerEvents: "none",
        }}
      />

      {/* overlay/blur (não depende dele para o fundo) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(14,31,38,0.86)",
          backdropFilter: isIOS ? "none" : "blur(14px)",
          WebkitBackdropFilter: isIOS ? "none" : "blur(14px)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: SAFE_B,
          height: TAB_H,
          zIndex: 1,
          display: "flex",
          alignItems: "stretch",
          ...containerStyle,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            width: compact ? "max-content" : "100%",
            minWidth: compact ? "100%" : "auto",
          }}
        >
          {items.map((it) => {
            const isActive = it.key === value;
            const Icon = it.icon;
            const handle = () => onChange?.(it.key, it);

            return (
              <button
                key={it.key}
                type="button"
                onClick={handle}
                style={{
                  ...tabStyle(isActive, compact),
                  flex: compact ? "0 0 auto" : 1,
                }}
                aria-current={isActive ? "page" : undefined}
              >
                {Icon ? (
                  <span style={{ opacity: isActive ? 1 : 0.85, lineHeight: 0 }}>
                    <Icon size={18} color={isActive ? THEME.text : THEME.muted} />
                  </span>
                ) : null}
                <div style={{ lineHeight: "11px", whiteSpace: "nowrap" }}>{it.label}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* esconde scrollbar no webkit (quando compact) */}
      <style>{`
        nav[aria-label="Submenu"] *::-webkit-scrollbar { display: none; }
      `}</style>
    </nav>
  );
}
